import React, { useState, useEffect, useCallback } from 'react';

const MainFormComponent = ({ 
  phoneNumber, 
  userInfo, // Add userInfo prop
  accessToken, // Add accessToken prop
  token, 
  customFormUrl, 
  onSubmit, 
  onLogout,
  resultPageUrl,
  campaignInfo // Add campaignInfo prop
}) => {
  const [iframeLoading, setIframeLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Extract campaign code from URL path
  const extractCampaignCode = useCallback(() => {
    const pathname = window.location.pathname;
    console.log('🔍 Extracting campaign code from pathname:', pathname);
    
    // Extract campaign code from path like /cd2, /campaign123, /chien-dich/cd3, etc.
    const campaignMatch = pathname.match(/^\/(?:[a-zA-Z0-9_-]+\/)?([a-zA-Z0-9_-]+)$/);
    if (campaignMatch) {
      const campaignCode = campaignMatch[1];
      console.log('✅ Campaign code extracted:', campaignCode);
      return campaignCode;
    }
    
    console.log('❌ No campaign code found in pathname');
    return null;
  }, []);

  // Send user info to iframe when loaded
  const sendUserInfoToIframe = useCallback(() => {
    if (!customFormUrl) return;
    
    try {
      const iframe = document.getElementById('custom-form-iframe');
      if (iframe && iframe.contentWindow) {
        const userInfoToSend = {
          type: 'USER_INFO',
          phoneNumber: phoneNumber,
          token: token,
          accessToken: accessToken, // Include accessToken
          // Add campaign info if available
          ...(campaignInfo && {
            campaignCode: extractCampaignCode(),
            campaignInfo: campaignInfo
          }),
          // Add detailed user info if available
          ...(userInfo && {
            name: userInfo.name || userInfo.fullName || userInfo.displayName,
            email: userInfo.email,
            id: userInfo.id,
            avatar: userInfo.avatar || userInfo.profileImage,
            // Add any other user fields that might be useful
            ...userInfo
          })
        };
        
        iframe.contentWindow.postMessage(userInfoToSend, '*');
        console.log('Sent user info to iframe:', {
          ...userInfoToSend,
          accessToken: accessToken ? accessToken.substring(0, 20) + '...' : 'None'
        });
      }
    } catch (error) {
      console.warn('Could not send data to iframe:', error);
    }
  }, [customFormUrl, phoneNumber, token, userInfo, accessToken, campaignInfo, extractCampaignCode]);

  // Handle form submission from iframe
  const handleFormSubmit = useCallback(async (formData) => {
    console.log('🚀 MainFormComponent handleFormSubmit called with:', formData);
    
    if (!onSubmit) {
      console.error('❌ onSubmit callback not provided');
      return;
    }
    
    setSubmitLoading(true);
    try {
      console.log('📤 Calling onSubmit callback...');
      await onSubmit(formData);
      console.log('✅ onSubmit callback completed');
    } catch (error) {
      console.error('❌ Form submission error:', error);
    } finally {
      setSubmitLoading(false);
    }
  }, [onSubmit]);

  // Handle logout from iframe
  const handleLogout = useCallback(() => {
    if (window.confirm('Bạn có chắc chắn muốn đăng xuất?')) {
      if (onLogout) {
        onLogout();
      }
    }
  }, [onLogout]);

  // Handle iframe communication
  useEffect(() => {
    const handleMessage = (event) => {
      // Basic security check - in production, verify origin
      try {
        console.log('🔍 MainFormComponent received message:', event.data);
        
        if (event.data.type === 'SUBMIT_FORM') {
          console.log('📝 Form submission received:', event.data.formData);
          handleFormSubmit(event.data.formData);
        } else if (event.data.type === 'LOGOUT') {
          console.log('🚪 Logout request received');
          handleLogout();
        } else if (event.data.type === 'FORM_READY') {
          console.log('✅ Form ready message received');
          // Form has loaded and is ready for data
          sendUserInfoToIframe();
        }
      } catch (error) {
        console.warn('Invalid iframe message:', error);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleFormSubmit, handleLogout, sendUserInfoToIframe]); // eslint-disable-line react-hooks/exhaustive-deps



  // Handle iframe load event
  const handleIframeLoad = () => {
    setIframeLoading(false);
    // Small delay to ensure iframe is fully rendered
    setTimeout(() => {
      sendUserInfoToIframe();
    }, 100);
  };











  // Error state
  if (!customFormUrl) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-50 to-brand-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-8 text-center max-w-md">
          <h1 className="text-xl font-bold text-red-600 mb-4">Lỗi cấu hình</h1>
          <p className="text-gray-600 mb-4">Không tìm thấy URL của custom form</p>
          <button
            onClick={handleLogout}
            className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600"
          >
            Quay lại đăng nhập
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 to-brand-100">
      <div className="max-w-full mx-auto">
        {/* Form Status Bar (if needed) */}
        {(iframeLoading || submitLoading) && (
          <div className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-3">
            <div className="max-w-7xl mx-auto">
              {submitLoading && (
                <div className="flex items-center gap-2 text-sm text-brand-600">
                  <div className="w-4 h-4 border-2 border-brand-600 border-t-transparent rounded-full animate-spin"></div>
                  <span>Đang xử lý form...</span>
                </div>
              )}
              
              {iframeLoading && !submitLoading && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-xs">
                    <div 
                      className="bg-brand-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: '30%' }}
                    ></div>
                  </div>
                  <span>Đang tải form...</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Custom Form Iframe Container */}
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-lg shadow-md relative overflow-hidden" style={{ minHeight: '70vh' }}>
              {/* Loading overlay */}
              {iframeLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
                  <div className="text-center">
                    <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-gray-600">Đang tải form...</p>
                    <p className="text-xs text-gray-500 mt-1">Vui lòng đợi trong giây lát</p>
                  </div>
                </div>
              )}
              
              {/* Custom form iframe */}
              <iframe
                id="custom-form-iframe"
                src={customFormUrl}
                className="w-full h-full border-0"
                onLoad={handleIframeLoad}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                title="Custom Form"
                style={{ minHeight: '70vh' }}
              />
              
              {/* Submit overlay */}
              {submitLoading && (
                <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center z-20">
                  <div className="bg-white rounded-lg p-6 text-center shadow-xl">
                    <div className="w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Đang xử lý form</h3>
                    <p className="text-gray-600">Vui lòng không tắt trình duyệt...</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainFormComponent;
